import { yupResolver } from "@hookform/resolvers/yup";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import ControlledSelectSeparatedLabel from "../../../../components/controlled/ControlledSelectSeparatedLabel";
import ControlledTextFieldSeparatedLabel from "../../../../components/controlled/ControlledTextFieldSeparatedLabel";
import { useAddMailingIntegrationMutation } from "../../integrationsApiSlice.js";

import { useTranslation } from "react-i18next";
import BackdropLoader from "../../../../components/BackdropLoader/index.jsx";
import { DisconnectSMTP } from "./Disconnect.jsx";
import { getFormSchema } from "./formSchema.js";

export const MailingIntegration = ({
	user,
	isLoadingUser,
	hasActiveMicrosoftOauth,
	hasActiveSMTPConnection,
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [addMailingIntegrationMutation, { isLoading }] =
		useAddMailingIntegrationMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
		setValue,
	} = useForm({
		defaultValues: {
			emailReplyFrom: user?.mailingSettings?.emailReplyFrom || "",
			emailUsername: user?.mailingSettings?.emailUsername || "",
			emailPassword: user?.mailingSettings?.emailPassword || "",
			smtpHost: user?.mailingSettings?.smtpHost || "",
			smtpPort: user?.mailingSettings?.smtpPort || "",
			smtpEncryption: user?.mailingSettings?.smtpEncryption || "",
			smtpValidateCert: String(user?.mailingSettings?.smtpValidateCert) || "",
			imapHost: user?.mailingSettings?.imapHost || "",
			imapPort: user?.mailingSettings?.imapPort || "",
			imapEncryption: user?.mailingSettings?.imapEncryption || "",
			imapValidateCert: String(user?.mailingSettings?.imapValidateCert) || "",
		},
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	useEffect(() => {
		if (user) {
			setValue("emailReplyFrom", user?.mailingSettings?.emailReplyFrom || "");
			setValue("emailUsername", user?.mailingSettings?.emailUsername || "");
			setValue("emailPassword", user?.mailingSettings?.emailPassword || "");
			setValue("smtpHost", user?.mailingSettings?.smtpHost || "");
			setValue("smtpPort", user?.mailingSettings?.smtpPort || "");
			setValue("smtpEncryption", user?.mailingSettings?.smtpEncryption || "");
			setValue(
				"smtpValidateCert",
				String(user?.mailingSettings?.smtpValidateCert) || "",
			);
			setValue("imapHost", user?.mailingSettings?.imapHost || "");
			setValue("imapPort", user?.mailingSettings?.imapPort || "");
			setValue("imapEncryption", user?.mailingSettings?.imapEncryption || "");
			setValue(
				"imapValidateCert",
				String(user?.mailingSettings?.imapValidateCert) || "",
			);
		}
	}, [user, setValue]);

	const onSubmit = async (data) => {
		await addMailingIntegrationMutation(data)
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_smtp_integration"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	if (isLoadingUser) return <BackdropLoader isOpen={true} />;

	return (
		<>
			<Box>
				<Box
					component="form"
					autoComplete="off"
					noValidate
					onSubmit={handleSubmit(onSubmit)}
					sx={{ p: 0, marginTop: "10px" }}
				>
					<input type="text" style={{ display: "none" }} aria-hidden="true" />
					<input
						type="password"
						style={{ display: "none" }}
						aria-hidden="true"
					/>
					<Grid container spacing={5}>
						<Grid size={6}>
							<Grid container spacing={3.5}>
								{/* DEFAULT */}
								<Grid size={12}>
									<Box
										sx={{
											border: "1px solid #595959",
											padding: "20px 10px 20px 20px",
											position: "relative",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: "12px",
												transform: "translateY(-50%)",
												height: "28px",
												lineHeight: "28px",
												background: "#595959",
												padding: "0 10px",
												color: "#fff",
											}}
										>
											{t("basic_data")}
										</Box>
										<Grid container spacing={2} size={12}>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="emailReplyFrom"
													type="email"
													label={t("sender_email")}
													error={!!errors?.emailReplyFrom}
													labelXs={6}
													fieldXs={6}
													autoComplete="off emailReplyFrom new-password"
													inputProps={{
														autoComplete: "off emailReplyFrom new-password",
													}}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="emailUsername"
													type="text"
													label={t("email_username")}
													error={!!errors?.emailUsername}
													labelXs={6}
													fieldXs={6}
													autoComplete="off emailUsername new-password"
													inputProps={{
														autoComplete: "off emailUsername new-password",
													}}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="emailPassword"
													type="password"
													label={t("email_password")}
													error={!!errors?.emailPassword}
													labelXs={6}
													fieldXs={6}
													autoComplete="off emailPassword new-password"
													inputProps={{
														autoComplete: "off emailPassword new-password",
													}}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
										</Grid>
									</Box>
								</Grid>
								{/* SMTP */}
								<Grid size={12}>
									<Box
										sx={{
											border: "1px solid #0069b4",
											padding: "20px 10px 20px 20px",
											position: "relative",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: "12px",
												transform: "translateY(-50%)",
												height: "28px",
												lineHeight: "28px",
												background: "#0069b4",
												padding: "0 10px",
												color: "#fff",
											}}
										>
											{t("smtp")}
										</Box>
										<Grid container spacing={2} size={12}>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="smtpHost"
													type="text"
													label={t("smtp_host")}
													error={!!errors?.smtpHost}
													labelXs={6}
													fieldXs={6}
													autoComplete="off smtpHost new-password"
													inputProps={{
														autoComplete: "off smtpHost new-password",
													}}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="smtpPort"
													type="text"
													label={t("smtp_port")}
													error={!!errors?.smtpPort}
													labelXs={6}
													fieldXs={6}
													autoComplete="off smtpPort new-password"
													inputProps={{
														autoComplete: "off smtpPort new-password",
													}}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledSelectSeparatedLabel
													control={control}
													name="smtpEncryption"
													label={t("smtp_secure")}
													error={!!errors?.smtpEncryption}
													options={[
														{ value: "ssl", label: "SSL" },
														{ value: "tls", label: "TLS" },
														{ value: "false", label: t("lack") },
													].map((opt) => {
														return { value: opt.value, label: opt.label };
													})}
													labelXs={6}
													fieldXs={6}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledSelectSeparatedLabel
													control={control}
													name="smtpValidateCert"
													label={t("smtp_validate_cert")}
													error={!!errors?.smtpValidateCert}
													options={[
														{ value: "0", label: t("no") },
														{ value: "1", label: t("yes") },
													].map((opt) => {
														return { value: opt.value, label: opt.label };
													})}
													labelXs={6}
													fieldXs={6}
													disabled={hasActiveMicrosoftOauth}
												/>
											</Grid>
										</Grid>
									</Box>
								</Grid>
								{/* IMAP */}
								<Grid size={12}>
									<Box
										sx={{
											border: "1px solid #63c29a",
											padding: "20px 10px 20px 20px",
											position: "relative",
										}}
									>
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: "12px",
												transform: "translateY(-50%)",
												height: "28px",
												lineHeight: "28px",
												background: "#63c29a",
												padding: "0 10px",
												color: "#fff",
												zIndex: 102,
											}}
										>
											{t("imap")}
										</Box>
										<Box
											sx={{
												display: "none",
												position: "absolute",
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												background: "rgba(99, 194, 154, 0.7)",
												// display: "flex",
												justifyContent: "center",
												alignItems: "center",
												zIndex: 100,
											}}
										>
											<Typography
												sx={{
													// transform: "rotate(-14deg)",
													fontSize: "36px",
													pointerEvents: "none",
													color: "#fff",
												}}
											>
												{t("available_soon")}
											</Typography>
										</Box>
										<Grid container spacing={2} size={12}>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="imapHost"
													type="text"
													label={t("imap_host")}
													error={!!errors?.imapHost}
													labelXs={6}
													fieldXs={6}
													autoComplete="off imapHost new-password"
													inputProps={{
														autoComplete: "off imapHost new-password",
													}}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledTextFieldSeparatedLabel
													control={control}
													name="imapPort"
													type="text"
													label={t("imap_port")}
													error={!!errors?.imapPort}
													labelXs={6}
													fieldXs={6}
													autoComplete="off imapPort new-password"
													inputProps={{
														autoComplete: "off imapPort new-password",
													}}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledSelectSeparatedLabel
													control={control}
													name="imapEncryption"
													label={t("imap_secure")}
													error={!!errors?.imapEncryption}
													options={[
														{ value: "ssl", label: "SSL" },
														{ value: "tls", label: "TLS" },
														{ value: "false", label: t("lack") },
													].map((opt) => {
														return { value: opt.value, label: opt.label };
													})}
													labelXs={6}
													fieldXs={6}
												/>
											</Grid>
											<Grid size={12}>
												<ControlledSelectSeparatedLabel
													control={control}
													name="imapValidateCert"
													label={t("imap_validate_cert")}
													error={!!errors?.imapValidateCert}
													options={[
														{ value: "0", label: t("no") },
														{ value: "1", label: t("yes") },
													].map((opt) => {
														return { value: opt.value, label: opt.label };
													})}
													labelXs={6}
													fieldXs={6}
												/>
											</Grid>
										</Grid>
									</Box>
								</Grid>
								<Grid size={12}>
									<Box
										sx={{
											display: "flex",
											justifyContent: hasActiveSMTPConnection
												? "space-between"
												: "end",
											alignItems: "center",
											gap: "10px",
										}}
									>
										{hasActiveSMTPConnection && (
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: "20px",
													justifyContent: "start",
													alignItems: "start",
												}}
											>
												<DisconnectSMTP />
											</Box>
										)}
										<Button
											loading={isLoading}
											type="submit"
											variant="contained"
											disabled={hasActiveMicrosoftOauth}
										>
											{t("save")}
										</Button>
										{hasActiveMicrosoftOauth && (
											<Tooltip
												title={
													<Typography
														variant="p"
														sx={{ fontSize: "9pt" }}
														component="span"
													>
														{t("disconnect_ms_oauth_to_connect_smtp")}
													</Typography>
												}
												placement="top-end"
											>
												<WarningIcon color="warning" />
											</Tooltip>
										)}
									</Box>
								</Grid>
							</Grid>
						</Grid>
						<Grid size={6}>
							<Box
								sx={{
									border: "1px solid #FFB15D",
									color: "#7F7F7F",
									px: 5,
									py: 3,
								}}
							>
								<Typography
									component="h2"
									variant="h6"
									sx={{
										fontWeight: 600,
										display: "flex",
										alignItems: "start",
										gap: "4px",
									}}
								>
									<HelpOutlinedIcon
										sx={{ color: "#FFB15D", position: "relative", top: "4px" }}
									/>{" "}
									{t("smtp_info_general")}
								</Typography>
								<ul>
									<li>
										<b>{t("sender_email")}:</b> {t("smtp_info_sender_email")}
										<br />
										<i>{t("smtp_info_sender_email_example")}</i>
									</li>
									<li>
										<b>{t("email_username")}:</b>{" "}
										{t("smtp_info_email_username")}
										<br />
										<i>{t("smtp_info_email_username_example")}</i>.
									</li>
									<li>
										<b>{t("email_password")}:</b>{" "}
										{t("smtp_info_email_password")}
									</li>
									<li>
										<b>{t("smtp_host")}:</b> {t("smtp_info_smtp_host")}
										<br />
										<i>{t("smtp_info_smtp_host_example")}</i>
									</li>
									<li>
										<b>{t("smtp_port")}:</b> {t("smtp_info_smtp_port")}
										<br />
										<i>{t("smtp_info_smtp_port_example_1")}</i>.
										<br />
										<i>{t("smtp_info_smtp_port_example_2")}</i>
									</li>
									<li>
										<b>{t("smtp_secure")}:</b> {t("smtp_info_smtp_secure")}
										<br />
										<i>{t("smtp_info_smtp_secure_example")}</i>.
									</li>
									<li>
										<b>{t("smtp_validate_cert")}:</b>{" "}
										{t("smtp_info_smtp_validate_cert")}
									</li>
								</ul>
								<Typography
									component="h3"
									variant="h6"
									sx={{
										fontWeight: 600,
										display: "flex",
										alignItems: "center",
										gap: "4px",
									}}
								>
									<WarningAmberIcon sx={{ color: "#FFB15D" }} />{" "}
									{t("smtp_additional_info")}
								</Typography>
								<Typography>{t("smtp_additional_info_spam")}</Typography>
								<ul>
									<li>
										<b>{t("spf")}:</b> {t("smtp_additional_info_spf")}
									</li>
									<li>
										<b>{t("dkim")}:</b> {t("smtp_additional_info_dkim")}
									</li>
								</ul>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Box>
			<BackdropLoader isOpen={isLoadingUser} />
		</>
	);
};
